import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shopifyAPI } from '../services/api';
import { format, endOfYear, subYears } from 'date-fns';
import { useTenantUrl } from '../hooks/useTenantUrl';

const UnshippedReport: React.FC = () => {
  const { tenantNavigate } = useTenantUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allOrders, setAllOrders] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  
  // Get default date (last day of last year)
  const defaultDate = format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd');
  
  // Read filters from URL params, with defaults
  const urlDate = searchParams.get('date') || defaultDate;
  const urlStatus = searchParams.get('status') || '';
  
  // Local state for inputs (allows typing without URL updates on every keystroke)
  const [dateInput, setDateInput] = useState(urlDate);
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  
  // The date used for fetching (from URL)
  const asOfDate = urlDate;
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Sync local state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    setDateInput(urlDate);
    setStatusFilter(urlStatus);
  }, [urlDate, urlStatus]);

  // Update URL params helper
  const updateParams = useCallback((updates: { date?: string; status?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (updates.date !== undefined) {
      if (updates.date && updates.date !== defaultDate) {
        newParams.set('date', updates.date);
      } else {
        newParams.delete('date');
      }
    }
    
    if (updates.status !== undefined) {
      if (updates.status) {
        newParams.set('status', updates.status);
      } else {
        newParams.delete('status');
      }
    }
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, defaultDate]);

  // Fetch report on initial load only
  useEffect(() => {
    fetchReport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReport = async (date?: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await shopifyAPI.getUnshippedOrders(date || dateInput);
      setAllOrders(response.data.data);
      setSummary(response.data.summary);
      setCurrentPage(1); // Reset to first page on new fetch
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load unshipped orders');
    } finally {
      setLoading(false);
    }
  };

  // Get unique statuses for filter dropdown
  const availableStatuses = useMemo(() => {
    const statuses = new Set<string>();
    allOrders.forEach(order => {
      const status = order.fulfillment_status || 'unfulfilled';
      statuses.add(status);
    });
    return Array.from(statuses).sort();
  }, [allOrders]);

  // Filter orders based on selected status
  const filteredOrders = useMemo(() => {
    if (!statusFilter) return allOrders;
    return allOrders.filter(order => {
      const orderStatus = order.fulfillment_status || 'unfulfilled';
      return orderStatus === statusFilter;
    });
  }, [allOrders, statusFilter]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateInput(e.target.value);
  };

  const handleDateBlur = () => {
    // Update URL when user finishes typing
    if (dateInput !== urlDate) {
      updateParams({ date: dateInput });
    }
  };

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    updateParams({ status: newStatus });
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    // Sync URL with current input values and fetch
    updateParams({ date: dateInput, status: statusFilter });
    fetchReport(dateInput);
  };

  const handleExport = () => {
    shopifyAPI.exportUnshippedOrders(dateInput, statusFilter || undefined);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setDateInput(defaultDate);
    updateParams({ date: '', status: '' });
    setCurrentPage(1);
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  // Reset page when status filter changes via URL
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (loading) {
    return <div className="loading">Loading unshipped orders...</div>;
  }

  const hasActiveFilters = !!statusFilter;

  return (
    <div>
      <div className="page-header">
        <h1>Unshipped Orders (by date)</h1>
        <p>Orders that were paid but not yet shipped as of the selected date</p>
      </div>

      {/* Filter Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          alignItems: 'end'
        }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="asOfDate" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#4a5568' }}>
              As Of Date
            </label>
            <input
              type="date"
              id="asOfDate"
              className="form-control"
              value={dateInput}
              onChange={handleDateChange}
              onBlur={handleDateBlur}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="statusFilter" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#4a5568' }}>
              Fulfillment Status
            </label>
            <select
              id="statusFilter"
              className="form-control"
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">All Statuses</option>
              {availableStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={handleRefresh}>
              Refresh
            </button>
            {hasActiveFilters && (
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear Filters
              </button>
            )}
            {filteredOrders.length > 0 && (
              <button className="btn btn-secondary" onClick={handleExport}>
                📥 Export CSV
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '0.75rem', 
            backgroundColor: '#ebf8ff', 
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            color: '#2b6cb0'
          }}>
            Showing {filteredOrders.length} of {allOrders.length} orders
            {statusFilter && <span> • Status: <strong>{statusFilter}</strong></span>}
          </div>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      {/* Summary Stats */}
      {summary && (
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card">
            <h3>Unshipped Orders</h3>
            <div className="value" style={{ color: '#d69e2e' }}>
              {summary.total_count}
            </div>
            <div className="subvalue">
              Awaiting fulfillment
            </div>
          </div>

          <div className="stat-card">
            <h3>Total Amount</h3>
            <div className="value" style={{ color: '#d69e2e' }}>
              ${Number(summary.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="subvalue">
              Paid order amounts
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Unshipped Orders {filteredOrders.length > 0 && <span style={{ fontWeight: 400, fontSize: '1rem', color: '#718096' }}>({filteredOrders.length} total)</span>}</h2>
          {filteredOrders.length > itemsPerPage && (
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ color: '#718096', fontSize: '0.9rem' }}>
                Page {currentPage} of {totalPages}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  style={{ padding: '0.5rem 1rem' }}
                >
                  Previous
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  style={{ padding: '0.5rem 1rem' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        
        {filteredOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#38a169' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
              {hasActiveFilters ? 'No orders match your filters' : 'No unshipped orders!'}
            </div>
            <div style={{ marginTop: '0.5rem', color: '#718096' }}>
              {hasActiveFilters 
                ? 'Try adjusting your filter criteria'
                : `All paid orders were shipped by ${format(new Date(asOfDate), 'MMMM dd, yyyy')}`
              }
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Order Date</th>
                  <th>Customer</th>
                  <th>Total Amount</th>
                  <th>Paid Amount</th>
                  <th>Paid Date</th>
                  <th>Shipped Date</th>
                  <th>Days Waiting</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order: any) => {
                  const orderDate = new Date(order.order_date);
                  const daysWaiting = Math.floor((new Date(asOfDate).getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24));
                  const isLongWait = daysWaiting > 7;
                  const paymentsAsOfDate = Number(order.payments_as_of_date || 0);
                  
                  return (
                    <tr key={order.id} style={{
                      backgroundColor: isLongWait ? '#fffbeb' : 'transparent'
                    }}>
                      <td>
                        <button
                          onClick={() => tenantNavigate(`/orders/${order.id}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#4299e1',
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            padding: 0
                          }}
                        >
                          {order.name || `#${order.order_number}`}
                        </button>
                      </td>
                      <td>{format(orderDate, 'MMM dd, yyyy')}</td>
                      <td>{order.customer_email || 'N/A'}</td>
                      <td style={{ fontWeight: 600 }}>
                        ${Number(order.total_amount).toFixed(2)}
                      </td>
                      <td>
                        <span className="status-badge status-reconciled">
                          ${paymentsAsOfDate.toFixed(2)}
                        </span>
                      </td>
                      <td style={{ color: '#38a169' }}>
                        {order.first_paid_date 
                          ? format(new Date(order.first_paid_date), 'MMM dd, yyyy')
                          : '—'}
                      </td>
                      <td style={{ color: order.fulfillment_date ? '#38a169' : '#a0aec0' }}>
                        {order.fulfillment_date 
                          ? format(new Date(order.fulfillment_date), 'MMM dd, yyyy')
                          : '—'}
                      </td>
                      <td style={{
                        color: isLongWait ? '#d69e2e' : '#718096',
                        fontWeight: isLongWait ? 'bold' : 'normal'
                      }}>
                        {daysWaiting} days
                      </td>
                      <td>
                        <span className={`status-badge ${
                          order.fulfillment_status === 'fulfilled' ? 'status-reconciled' :
                          order.fulfillment_status === 'partial' ? 'status-pending' :
                          'status-unmapped'
                        }`}>
                          {order.fulfillment_status || 'unfulfilled'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {allOrders.length > 0 && (
        <div className="card">
          <h2>Understanding This Report</h2>
          <div style={{ lineHeight: '1.8' }}>
            <p><strong>How it works:</strong> This report shows orders that were fully paid but not yet shipped <em>as of</em> the selected date. It checks:</p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>Order was created by the selected date</li>
              <li>Payment transactions recorded up to that date cover the full order amount</li>
              <li>Order was NOT fulfilled/shipped by that date</li>
              <li>Excludes voided and fully refunded orders</li>
            </ul>
            <p><strong>Paid Amount:</strong> The total amount of successful (non-voided) payment transactions recorded on or before the selected date.</p>
            <p><strong>Days Waiting:</strong> Number of days between the order date and the selected date.</p>
            <p><strong>Status:</strong> The current fulfillment status of the order in Shopify.</p>
            <p style={{ marginTop: '1rem', color: '#718096' }}>
              <strong>Note:</strong> Orders waiting more than 7 days are highlighted in yellow. Some of these may have been shipped after the selected date.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnshippedReport;

